export const DEFAULT_DOCUMENTS_PAGE_SIZE = 50;
export const MAX_DOCUMENTS_PAGE_SIZE = 200;

// File validation constants
export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export function validateFile(file: File): { isValid: boolean; error?: string } {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size exceeds maximum allowed size of ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB`,
    };
  }

  return { isValid: true };
}

export interface DocumentBreadcrumbItem {
  id: string;
  name: string;
}

export interface DocumentFolderDto {
  id: string;
  name: string;
  parentId: string | null;
  createdAt: string;
}

export interface DocumentFolder {
  id: string;
  name: string;
  parentId: string | null;
  createdAt: string;
  type: 'folder';
}

export interface DocumentFileObjectDto {
  id: string;
  fileId?: string;
  languageId?: number | null;
  fileSize?: number | null;
  isAvailable?: boolean;
  updatedAt?: string;
  deletedAt?: string | null;
  metadata?: Record<string, unknown>;
  name?: string;
  originalFileName?: string | null;
}

export interface DocumentFileObject {
  id: string;
  fileId: string;
  name: string;
  originalFileName: string | null;
  languageId: number | null;
  fileSize: number | null;
  isAvailable: boolean;
  updatedAt: string | null;
  deletedAt: string | null;
  metadata: Record<string, unknown>;
}

export type DocumentUploadStatus = 'idle' | 'uploading' | 'failed' | 'uploaded';

export interface DocumentUploadState {
  status: DocumentUploadStatus;
  progress: number;
}

export interface DeviceDocumentAccess {
  documentId: string;
  deviceModelId: number;
  userLevel: number;
  createdAt: string;
  createdBy: string;
}

export interface DocumentFileDto {
  id: string;
  name: string;
  fileType: string | null;
  category: number;
  folderId: string | null;
  userId: string;
  createdAt: string;
  updatedAt: string;
  deviceAccess: DeviceDocumentAccess[];
  fileObjects?: DocumentFileObjectDto[];
  fileObject: DocumentFileObjectDto;
}

export interface DocumentFile {
  id: string;
  name: string;
  fileType: string | null;
  category: number;
  folderId: string | null;
  userId: string;
  createdAt: string;
  updatedAt: string;
  deviceAccess: DeviceDocumentAccess[];
  fileObject?: DocumentFileObject;
  fileObjects?: DocumentFileObject[];
  uploadState?: DocumentUploadState;
  type: 'file'
}

export interface CreateDocumentFileInput {
  folderId: string | null;
  name: string;
  fileType?: string;
  category: number;
  companyLevel?: string;
  deviceModelId?: number;
  userLevel?: number;
}
export interface CreateDocumentFileObjectInput {
  languageId: number | null;
  fileSize: number;
  name?: string;
  originalFileName: string;
}



export interface UploadUrlResponseDto {
  url: string;
  storedFileId: string;
}

export interface FolderContentsResponseDto {
  folders: DocumentFolderDto[];
  folderNextCursor?: string | null;
  files: DocumentFileDto[];
  fileNextCursor?: string | null;
  currentFolder?: DocumentBreadcrumbItem | null;
}

export interface FolderContentsResponse {
  folders: DocumentFolder[];
  folderNextCursor: string | null;
  files: DocumentFile[];
  fileNextCursor: string | null;
  currentFolder: DocumentBreadcrumbItem | null;
}

export interface FolderContentsParams {
  limit?: number;
  search?: string;
  folderCursor?: string;
  fileCursor?: string;
}

export interface DocumentContentsPageParam {
  folderCursor?: string;
  fileCursor?: string;
}

export interface DocumentExplorerLocationState {
  documentBreadcrumbs?: DocumentBreadcrumbItem[];
}

export type DocumentPermission = 'admin' | 'read';

export interface CompanyAccess {
  documentId: string;
  companyLevel: string;
  createdBy: string;
  createdAt: string;
}

export interface FileDetailDto {
  file: DocumentFileDto;
  fileObjects: DocumentFileObjectDto[];
  companyAccess: {
    documentId: string;
    companyLevel: string;
    category: number;
    ownerCompanyId: string;
    createdBy: string;
    createdAt: string;
  } | null;
  deviceAccess: DeviceDocumentAccess[];
  folderId?: string | null;
  // Defined when the file has public sharing enabled; undefined means sharing is off.
  shareLink?: string;
}

export interface FileDetail {
  file: DocumentFile;
  fileObjects: DocumentFileObject[];
  companyAccess: CompanyAccess | null;
  deviceAccess: DeviceDocumentAccess[];
  folderId: string | null;
  // Defined when the file has public sharing enabled; undefined means sharing is off.
  shareLink?: string;
}

export interface UpdateDocumentFileInput {
  name?: string;
  folderId?: string | null;
  category?: number;
  companyLevel?: string;
  accessCategory?: number;
}

export interface UpdateDocumentFileObjectInput {
  languageId?: number | null;
  fileSize?: number;
  name?: string;
  originalFileName?: string;
}

declare module '@tanstack/history' {
  interface HistoryState {
    documentBreadcrumbs?: DocumentBreadcrumbItem[];
  }
}
